import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/PostCard";
import EditPostModal from "@/components/EditPostModal";
import LinkPollModal from "@/components/LinkPollModal";
import PurgePostModal from "@/components/PurgePostModal";
import SiteSettingsPanel from "@/components/SiteSettingsPanel";
import TickerPanel from "@/components/TickerPanel";
import AnalyticsPanel from "@/components/AnalyticsPanel";
import UsersPanel from "@/components/UsersPanel";
import PollOrderPanel from "@/components/PollOrderPanel";
import { toast } from "sonner";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export default function AdminPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [scope, setScope] = useState("posts"); // posts | comments | settings
  const [items, setItems] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [linking, setLinking] = useState(null);
  const [purging, setPurging] = useState(null);

  useEffect(() => {
    if (!loading && (user === false || user?.role !== "admin")) {
      navigate("/");
    }
  }, [loading, user, navigate]);

  const load = () => {
    if (scope === "settings" || scope === "ticker" || scope === "analytics" || scope === "users" || scope === "polls-order") return;
    setRefreshing(true);
    const url = scope === "posts" ? "/admin/posts" : "/admin/comments";
    api.get(url, { params: { status: tab } })
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setRefreshing(false));
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, scope, user]);

  const moderate = async (id, action) => {
    const url = scope === "posts" ? `/admin/posts/${id}/moderate` : `/admin/comments/${id}/moderate`;
    try {
      await api.post(url, { action });
      toast.success(`Marked ${action === "approve" ? "approved" : "rejected"}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const togglePin = async (post) => {
    try {
      await api.post(`/admin/posts/${post.id}/pin`, { pinned: !post.pinned });
      toast.success(post.pinned ? "Unpinned." : "Pinned under lead.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleForumClose = async (post) => {
    try {
      await api.post(`/admin/posts/${post.id}/close-forum`, { closed: !post.closed });
      toast.success(post.closed ? "Forum reopened." : "Forum closed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading || !user || user.role !== "admin") {
    return <Layout><div className="max-w-3xl mx-auto px-4 py-24 mono text-sm text-neutral-500">Loading&hellip;</div></Layout>;
  }

  return (
    <Layout>
      <SEO path="/admin" title="Editor's Desk" noindex description="Editor's Desk on What Do We Want? When Do We Want It?" />
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mono text-xs uppercase tracking-[0.3em] text-[#fc4707]">Editor&rsquo;s Desk</div>
        <h1 className="serif text-5xl mt-3 tracking-tight">Moderation</h1>
        <p className="text-neutral-700 mt-3">Approve or reject citizen submissions. Only approved items appear on the front page.</p>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-b border-black pb-4">
          <div className="flex gap-2">
            {["posts", "comments", "analytics", "users", "polls-order", "settings", "ticker"].map((s) => (
              <button
                key={s}
                data-testid={`admin-scope-${s}`}
                onClick={() => setScope(s)}
                className={`mono text-xs uppercase tracking-[0.2em] px-4 py-2 border ${
                  scope === s ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"
                }`}
              >
                {s === "settings" ? "Site copy" : s === "ticker" ? "Ticker" : s === "analytics" ? "Analytics" : s === "users" ? "Members" : s === "polls-order" ? "Poll order" : s}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            {scope !== "settings" && scope !== "ticker" && scope !== "analytics" && scope !== "users" && scope !== "polls-order" && TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`mono text-xs uppercase tracking-[0.2em] px-4 py-2 border ${
                  tab === t.key ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          {scope === "settings" ? (
            <SiteSettingsPanel />
          ) : scope === "ticker" ? (
            <TickerPanel />
          ) : scope === "analytics" ? (
            <AnalyticsPanel />
          ) : scope === "users" ? (
            <UsersPanel />
          ) : scope === "polls-order" ? (
            <PollOrderPanel />
          ) : (
            <>
              {refreshing && <p className="mono text-xs text-neutral-500 mb-4">Refreshing&hellip;</p>}
              {items.length === 0 ? (
                <div className="border border-dashed border-neutral-400 p-12 text-center">
                  <p className="serif text-2xl">Nothing to moderate here.</p>
                  <p className="text-neutral-600 mt-2 mono text-xs uppercase tracking-[0.25em]">Queue empty</p>
                </div>
              ) : (
                <div className="border border-black divide-y divide-neutral-200">
              {items.map((it) => (
                <div key={it.id} className="p-5 hover:bg-neutral-50 transition-colors" data-testid={`admin-item-${it.id}`}>
                  <div className="flex items-baseline justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                        {scope === "posts" ? (
                          <>
                            <span className="text-amber-700">{it.type}</span>
                            <StatusBadge status={it.status} />
                          </>
                        ) : (
                          <>
                            <span className="text-amber-700">Reply</span>
                            <span>/</span>
                            <span>on &ldquo;{it.post?.title || "—"}&rdquo;</span>
                            <StatusBadge status={it.status} />
                          </>
                        )}
                      </div>
                      {scope === "posts" ? (
                        <Link to={`/post/${it.id}`} className="serif text-2xl block mt-2 edi-link">
                          {it.title}
                        </Link>
                      ) : (
                        <p className="mt-2 text-neutral-800 whitespace-pre-wrap">{it.body}</p>
                      )}
                      <div className="mt-2 mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                        by {it.author?.name}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {scope === "posts" && it.type === "forum" && it.status === "approved" && (
                        <button
                          data-testid={`admin-close-${it.id}`}
                          onClick={() => toggleForumClose(it)}
                          title={it.closed ? "Reopen the forum for new replies" : "Close the forum to new replies"}
                          className={`border px-4 py-2 mono text-xs uppercase tracking-[0.2em] transition-colors ${it.closed ? "border-amber-700 text-amber-800 hover:bg-amber-700 hover:text-white" : "border-black hover:bg-black hover:text-white"}`}
                        >
                          {it.closed ? "Reopen" : "Close"}
                        </button>
                      )}
                      {scope === "posts" && it.status === "approved" && (
                        <button
                          data-testid={`admin-pin-${it.id}`}
                          onClick={() => togglePin(it)}
                          title={it.pinned ? "Unpin from Front Page" : "Pin under lead on Front Page"}
                          className={`border px-4 py-2 mono text-xs uppercase tracking-[0.2em] transition-colors ${it.pinned ? "bg-amber-700 text-white border-amber-700 hover:bg-amber-800" : "border-black hover:bg-black hover:text-white"}`}
                        >
                          {it.pinned ? "Pinned ★" : "Pin"}
                        </button>
                      )}
                      {scope === "posts" && (
                        <button
                          data-testid={`admin-edit-${it.id}`}
                          onClick={() => setEditing(it)}
                          className="border border-black px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors"
                        >
                          Edit
                        </button>
                      )}
                      {scope === "posts" && it.type !== "poll" && (
                        <button
                          data-testid={`admin-linkpoll-${it.id}`}
                          onClick={() => setLinking(it)}
                          title="Append a poll link to this piece"
                          className="border border-black px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors"
                        >
                          + Link poll
                        </button>
                      )}
                      {tab !== "approved" && (
                        <button
                          data-testid={`admin-approve-${it.id}`}
                          onClick={() => moderate(it.id, "approve")}
                          className="bg-emerald-800 text-white px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-emerald-900 transition-colors"
                        >
                          Approve
                        </button>
                      )}
                      {tab !== "rejected" && (
                        <button
                          data-testid={`admin-reject-${it.id}`}
                          onClick={() => moderate(it.id, "reject")}
                          className="bg-red-900 text-white px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-red-950 transition-colors"
                        >
                          Reject
                        </button>
                      )}
                      {scope === "posts" && tab === "rejected" && (
                        <button
                          data-testid={`admin-purge-${it.id}`}
                          onClick={() => setPurging(it)}
                          title="Permanently delete from the database"
                          className="border border-red-700 text-red-700 px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-red-700 hover:text-white transition-colors"
                        >
                          Purge
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
              )}
            </>
          )}
        </div>
      </div>
      {editing && (
        <EditPostModal
          post={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {linking && (
        <LinkPollModal
          post={linking}
          onClose={() => setLinking(null)}
          onSaved={() => { setLinking(null); load(); }}
        />
      )}
      {purging && (
        <PurgePostModal
          post={purging}
          onClose={() => setPurging(null)}
          onPurged={() => { setPurging(null); load(); }}
        />
      )}
    </Layout>
  );
}
